// assets/eos-model-secici.js
import { Component } from '@theme/component';

const MODELS = ['kadin', 'erkek'];
const STORAGE_KEY = 'eos-model';

/**
 * Kadın / Erkek model switch for the shared product pool.
 *
 * The visible colours are filtered by CSS keyed on <html data-eos-model>, so
 * this component only decides the model and keeps the selected colour valid.
 * Deciding order: fixed model (single-gender product) > ?model= or the
 * collection path (already written to <html> by eos-renk-sistemi) > the
 * selected variant's colour when only one model sells it > the model chosen
 * earlier in this session > the editor default.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [secenekler]
 * @extends Component<Refs>
 */
class EosModelSecici extends Component {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #pending = null;

  /** @returns {DOMStringMap} Settings live on the inner wrapper the block renders. */
  get #config() {
    return this.querySelector('.eos-model')?.dataset ?? this.dataset;
  }

  /** @returns {{ kadin: string[], erkek: string[] }} Allowed colour handles per model. */
  get #allowed() {
    const read = (/** @type {string | undefined} */ raw) => (raw ?? '').split(',').filter(Boolean);
    return { kadin: read(this.#config.kadin), erkek: read(this.#config.erkek) };
  }

  connectedCallback() {
    super.connectedCallback();
    this.#apply(this.#initialModel(), false);

    // Horizon re-renders the picker from a context-free URL after every
    // choice. The CSS rule survives that, but the re-render can land on a
    // colour this model does not offer, so re-check after each one.
    const section = this.closest('.shopify-section');
    if (section) {
      this.#observer = new MutationObserver(() => this.#schedule());
      this.#observer.observe(section, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    if (this.#pending !== null) clearTimeout(this.#pending);
  }

  /** @param {MouseEvent} event */
  sec(event) {
    const button = /** @type {HTMLElement | null} */ (event.target);
    const model = button?.dataset.model;
    if (model && MODELS.includes(model)) this.#apply(model, true);
  }

  #schedule() {
    if (this.#pending !== null) return;
    this.#pending = window.setTimeout(() => {
      this.#pending = null;
      this.#ensureVisibleColour();
    }, 60);
  }

  /** @returns {string} */
  #initialModel() {
    const fixed = this.#config.sabit;
    if (fixed && MODELS.includes(fixed)) return fixed;

    const fromPage = document.documentElement.dataset.eosModel;
    if (fromPage && MODELS.includes(fromPage)) return fromPage;

    const hinted = this.#modelFromSelectedColour();
    if (hinted) return hinted;

    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored && MODELS.includes(stored)) return stored;
    } catch {
      // Storage can be blocked; the default below still gives a valid model.
    }

    const fallback = this.#config.varsayilan ?? '';
    return MODELS.includes(fallback) ? fallback : 'kadin';
  }

  /**
   * A shared link to a black variant should open on the model that sells it.
   * @returns {string | null}
   */
  #modelFromSelectedColour() {
    const handle = this.#selectedColourLabel()?.dataset.eosRenk;
    if (!handle) return null;
    const { kadin, erkek } = this.#allowed;
    if (kadin.includes(handle) && !erkek.includes(handle)) return 'kadin';
    if (erkek.includes(handle) && !kadin.includes(handle)) return 'erkek';
    return null;
  }

  /**
   * @param {string} model
   * @param {boolean} remember
   */
  #apply(model, remember) {
    document.documentElement.dataset.eosModel = model;
    if (remember) {
      try {
        sessionStorage.setItem(STORAGE_KEY, model);
      } catch {
        // Not remembering across pages is acceptable.
      }
    }
    for (const button of this.refs.secenekler ?? []) {
      button.setAttribute('aria-checked', String(button.dataset.model === model));
    }
    this.#ensureVisibleColour();
  }

  /** @returns {HTMLElement | null} The label of the checked colour radio. */
  #selectedColourLabel() {
    const section = this.closest('.shopify-section') ?? document;
    const name = this.#config.renkSecenegi ?? 'Renk';
    const checked = section.querySelector(
      `variant-picker input[type="radio"][data-option-name="${CSS.escape(name)}"]:checked`
    );
    return checked?.closest('[data-eos-renk]') ?? null;
  }

  /** Moves the selection onto an offered colour when the current one is hidden. */
  #ensureVisibleColour() {
    const current = this.#selectedColourLabel();
    if (!current || getComputedStyle(current).display !== 'none') return;

    const siblings = current.parentElement?.querySelectorAll('[data-eos-renk]') ?? [];
    const next = [...siblings].find((label) => getComputedStyle(label).display !== 'none');
    next?.querySelector('input')?.click();
  }
}

if (!customElements.get('eos-model-secici-component')) {
  customElements.define('eos-model-secici-component', EosModelSecici);
}
